import {
    ChevronRight,
    Sparkles,
} from "lucide-react";
import {
    useEffect,
    useRef,
    useState,
    useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../Redux/hooks";
import {
    mobileHeroBannersFallback,
    mobileShowcaseCardsFallback,
    MobileHeroBanner,
    MobileShowcaseCard,
    MobileShowcaseItem,
} from "./MobileHomeHeroShowcaseData";
import "./MobileHomeHeroShowcase.css";

const AUTO_SCROLL_DELAY = 4500;

const mapProductToShowcaseItem = (p: any): MobileShowcaseItem => ({
    id: p._id || p.id || "",
    name: p.name || "Product",
    image: p.mainImage || (Array.isArray(p.images) && p.images[0]) || "/assets/images/ban-pro/ban-pro-1.png",
});

const MobileHomeHeroShowcase = () => {
    const navigate = useNavigate();
    const bannerScrollRef = useRef<HTMLDivElement>(null);
    const [activeBanner, setActiveBanner] = useState(0);
    const [isBannerPaused, setIsBannerPaused] = useState(false);

    const storefrontAds = useAppSelector((state) => state.storefrontAds);
    const { keepShopping } = useAppSelector((state) => state.activity);
    const { productsWithCoupons, productsWithDeals } = useAppSelector(
        (state) => state.eCommerceProducts,
    );

    // Derive banners from storefrontAds (home_feed), fallback to static
    const banners: MobileHeroBanner[] = useMemo(() => {
        const adSlots = storefrontAds?.slotsByPlacement?.home_feed || [];
        if (adSlots.length > 0) {
            return adSlots.slice(0, 5).map((slot, i) => ({
                id: slot.impressionId || i,
                image: slot.creative?.imageUrl || slot.creative?.logoUrl || `/assets/images/banner/banner-${(i % 3) + 1}.jpg`,
                alt: slot.productName || slot.advertiserName || "Salar banner",
                eyebrow: (slot.advertiserName || "SALAR").toUpperCase(),
                label: slot.creative?.headline || slot.productName || "Shop now",
            }));
        }
        return mobileHeroBannersFallback;
    }, [storefrontAds]);

    // Derive showcase cards from real data with fallback
    const showcaseCards: MobileShowcaseCard[] = useMemo(() => {
        const cards: MobileShowcaseCard[] = [];

        // Card 1: Continue shopping (from keepShopping)
        if (keepShopping && keepShopping.length > 0) {
            cards.push({
                id: "continue",
                title: "Pick up where you left off",
                kicker: "CONTINUE SHOPPING",
                theme: "blue",
                items: keepShopping.slice(0, 4).map(mapProductToShowcaseItem),
            });
        }

        // Card 2: Deals (from productsWithDeals)
        if (productsWithDeals && productsWithDeals.length > 0) {
            cards.push({
                id: "deals",
                title: "Top deals for you",
                kicker: "TRENDING DEALS",
                theme: "sand",
                items: productsWithDeals.slice(0, 4).map(mapProductToShowcaseItem),
            });
        }

        // Card 3: Coupon products
        if (productsWithCoupons && productsWithCoupons.length > 0) {
            cards.push({
                id: "coupons",
                title: "Save more with coupons",
                kicker: "COUPON PICKS",
                theme: "rose",
                items: productsWithCoupons.slice(0, 4).map(mapProductToShowcaseItem),
            });
        }

        // Ensure a rich swipeable rail by blending in fallback category clusters
        if (cards.length < 4) {
            const existingIds = new Set(cards.map((c) => c.id));
            const fallbacksToAdd = mobileShowcaseCardsFallback.filter(
                (fb) => !existingIds.has(fb.id)
            );
            cards.push(...fallbacksToAdd.slice(0, 5 - cards.length));
        }

        return cards.length > 0 ? cards : mobileShowcaseCardsFallback;
    }, [keepShopping, productsWithCoupons, productsWithDeals]);

    const scrollToBanner = (index: number, behavior: ScrollBehavior = "smooth") => {
        const container = bannerScrollRef.current;
        if (!container) return;
        const banners = container.querySelectorAll<HTMLElement>(".mobile-home-banner-card");
        const target = banners[index];
        if (!target) return;
        container.scrollTo({ left: target.offsetLeft - container.offsetLeft, behavior });
        setActiveBanner(index);
    };

    useEffect(() => {
        if (isBannerPaused || banners.length <= 1) return;
        const timer = window.setInterval(() => {
            setActiveBanner((current) => {
                const next = (current + 1) % banners.length;
                window.requestAnimationFrame(() => scrollToBanner(next, "smooth"));
                return next;
            });
        }, AUTO_SCROLL_DELAY);
        return () => window.clearInterval(timer);
    }, [isBannerPaused, banners.length]);

    const handleBannerScroll = () => {
        const container = bannerScrollRef.current;
        if (!container) return;
        const banners = Array.from(container.querySelectorAll<HTMLElement>(".mobile-home-banner-card"));
        if (banners.length === 0) return;
        const containerLeft = container.getBoundingClientRect().left;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        banners.forEach((banner, index) => {
            const distance = Math.abs(banner.getBoundingClientRect().left - containerLeft);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = index;
            }
        });
        setActiveBanner(closestIndex);
    };

    const handleBannerInteractionStart = () => setIsBannerPaused(true);
    const handleBannerInteractionEnd = () => {
        window.setTimeout(() => setIsBannerPaused(false), 1800);
    };

    const handleProductClick = (item: MobileShowcaseItem) => {
        if (item.id) {
            navigate(`/DetailPage/${item.id}`);
        }
    };

    const handleSeeMore = (card: MobileShowcaseCard) => {
        if (card.id === "continue") {
            navigate("/buy-again");
        } else if (card.id === "deals") {
            navigate("/todays-deals");
        } else {
            navigate("/Categories/All");
        }
    };

    return (
        <section className="mobile-home-showcase">
            <div className="mobile-home-banner-shell">
                <div
                    ref={bannerScrollRef}
                    className="mobile-home-banner-scroll"
                    onScroll={handleBannerScroll}
                    onTouchStart={handleBannerInteractionStart}
                    onTouchEnd={handleBannerInteractionEnd}
                    onMouseEnter={handleBannerInteractionStart}
                    onMouseLeave={handleBannerInteractionEnd}
                >
                    {banners.map((banner, index) => (
                        <article
                            className={`mobile-home-banner-card ${activeBanner === index ? "active" : ""}`}
                            key={banner.id}
                        >
                            <img src={banner.image} alt={banner.alt} />
                            <div className="mobile-home-banner-shade" />
                            <div className="mobile-home-banner-glow" />
                            <div className="mobile-home-banner-copy">
                                <span>{banner.eyebrow}</span>
                                <strong>{banner.label}</strong>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="mobile-home-banner-progress" aria-label="Banner navigation">
                    {banners.map((banner, index) => (
                        <button
                            type="button"
                            key={banner.id}
                            className={activeBanner === index ? "active" : ""}
                            onClick={() => scrollToBanner(index)}
                            aria-label={`Show banner ${index + 1}`}
                            aria-current={activeBanner === index ? "true" : undefined}
                        >
                            <span />
                        </button>
                    ))}
                </div>
            </div>

            <div className="mobile-home-personalized-heading">
                <div>
                    <span>
                        <Sparkles size={14} strokeWidth={1.9} />
                        MADE FOR YOU
                    </span>
                    <h2>More of what you like</h2>
                </div>
                <small>Swipe to explore</small>
            </div>

            <div className="mobile-home-cluster-scroll">
                {showcaseCards.map((card) => (
                    <article className={`mobile-home-cluster-card theme-${card.theme}`} key={card.id}>
                        <div className="mobile-home-cluster-heading">
                            <div>
                                <span>{card.kicker}</span>
                                <h3>{card.title}</h3>
                            </div>
                            <button
                                type="button"
                                aria-label={`See more ${card.title}`}
                                onClick={() => handleSeeMore(card)}
                            >
                                <ChevronRight size={16} strokeWidth={2.1} />
                            </button>
                        </div>

                        <div className="mobile-home-product-grid">
                            {card.items.map((item) => (
                                <button
                                    type="button"
                                    className="mobile-home-product-tile"
                                    key={item.id || item.name}
                                    onClick={() => handleProductClick(item)}
                                >
                                    <span className="mobile-home-product-image">
                                        <img src={item.image} alt={item.name} />
                                    </span>
                                    <strong>{item.name}</strong>
                                </button>
                            ))}
                        </div>

                        <button
                            type="button"
                            className="mobile-home-cluster-more"
                            onClick={() => handleSeeMore(card)}
                        >
                            <span>See all</span>
                            <ChevronRight size={15} strokeWidth={2} />
                        </button>
                    </article>
                ))}
            </div>
        </section>
    );
};

export default MobileHomeHeroShowcase;
